import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Layers, Ruler, Truck, ShieldCheck, ArrowRight, CheckCircle2,
  BadgeCheck, Tag, Factory, Headset,
} from "lucide-react";
import { Button } from "../components/ui/button";
import ProductRow from "../components/ProductRow";
import { HERO, INDUSTRIES, CATALOG, productsForIndustry, PRODUCT_RANGE, PARTNER_POINTS, STATS, IMAGES } from "../mock/mock";

const ICONS = { Layers, Ruler, Truck, ShieldCheck, BadgeCheck, Tag, Factory, Headset };

export default function Home({ onEnquire }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-6 pt-3 pb-8 lg:pt-4">
          <div className="flex flex-col justify-center max-w-xl reveal">
            <p className="text-[12px] font-semibold tracking-[0.25em] text-[#f4520b] mb-3">{HERO.eyebrow}</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-[56px] font-extrabold leading-[1.05] text-[#1c2e3a]">
              {HERO.titleLine1}<br />{HERO.titleLine2}
            </h1>
            <p className="mt-4 text-slate-600 text-[16px] leading-relaxed">{HERO.description}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={() => onEnquire()} className="bg-[#1c2e3a] hover:bg-[#29424f] text-white rounded-md px-7 h-12 text-[15px] font-semibold">
                Get a Quote
              </Button>
              <Button onClick={() => navigate("/industries")} variant="outline" className="border-slate-300 text-[#1c2e3a] hover:bg-slate-50 rounded-md px-7 h-12 text-[15px] font-semibold">
                Browse Catalog
              </Button>
            </div>
          </div>

          <div className="relative reveal">
            <div className="relative w-full rounded-xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-auto lg:h-full min-h-[320px]">
              <img src={IMAGES.heroBedroom} alt="Quality mattress in modern bedroom" className="w-full h-full object-cover" />
              <div className="absolute top-4 right-4 bg-[#1c2e3a]/95 text-white rounded-lg px-5 py-4 backdrop-blur-sm">
                {HERO.badgeList.map((b) => (
                  <div key={b} className="text-[12px] font-semibold tracking-wide leading-6">{b}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Highlights bar */}
        <div className="border-t border-slate-100">
          <div className="mx-auto max-w-7xl px-4 md:px-8 py-4 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {HERO.highlights.map((h) => {
              const Icon = ICONS[h.icon];
              return (
                <div key={h.title} className="flex items-center gap-3">
                  <div className="h-11 w-11 shrink-0 rounded-lg bg-[#fff0e9] flex items-center justify-center text-[#1c2e3a]">
                    <Icon size={20} />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-[#1c2e3a] leading-tight">{h.title}</p>
                    <p className="text-[12.5px] text-slate-500">{h.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRY-WISE CATALOG SECTIONS */}
      {INDUSTRIES.map((ind, idx) => {
        const prods = productsForIndustry(ind.slug);
        return (
          <section key={ind.slug} className={`${idx % 2 ? "bg-[#f7f9fc]" : "bg-white"} py-8 md:py-10`}>
            <div className="mx-auto max-w-7xl px-4 md:px-8">
              <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div className="max-w-2xl">
                  <p className="text-[11px] font-semibold tracking-[0.25em] text-[#f4520b] mb-1.5">INDUSTRY CATALOG</p>
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#1c2e3a]">{ind.title}</h2>
                  <p className="mt-1.5 text-slate-600 text-[15px]">{ind.tagline}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/industries/${ind.slug}`)}
                  className="group border-slate-300 text-[#1c2e3a] hover:bg-slate-50 rounded-md h-11 font-semibold"
                >
                  View All {prods.length} Products <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              <div className="space-y-4">
                {prods.filter((p) => p.featured).slice(0, 2).map((p) => (
                  <ProductRow key={p.id} product={p} onEnquire={onEnquire} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* PRODUCT RANGE */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={IMAGES.stacked} alt="Stack of mattresses" className="w-full rounded-xl shadow-xl object-cover aspect-[5/4]" />
          </div>
          <div>
            <p className="text-[12px] font-semibold tracking-[0.25em] text-[#f4520b] mb-3">{PRODUCT_RANGE.eyebrow}</p>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#1c2e3a] leading-tight">{PRODUCT_RANGE.title}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{PRODUCT_RANGE.desc}</p>
            <ul className="mt-6 space-y-3">
              {PRODUCT_RANGE.items.map((it) => (
                <li key={it} className="flex items-center gap-3 text-[15px] text-slate-700">
                  <CheckCircle2 size={20} className="text-[#1c2e3a]" /> {it}
                </li>
              ))}
            </ul>
            <Button onClick={() => navigate("/industries")} className="mt-7 bg-[#1c2e3a] hover:bg-[#29424f] text-white rounded-md px-6 h-12 font-semibold group">
              Browse by Industry <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* PARTNER */}
      <section id="why-us" className="relative py-16 md:py-24">
        <div className="absolute inset-0">
          <img src={IMAGES.warehouse} alt="Mattress warehouse" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#14212b]/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-md bg-white rounded-2xl shadow-2xl p-7 md:p-9">
            <h3 className="font-heading text-2xl md:text-[26px] font-extrabold text-[#1c2e3a]">Your Bulk Mattress Partner</h3>
            <ul className="mt-6 space-y-4">
              {PARTNER_POINTS.map((p) => {
                const Icon = ICONS[p.icon];
                return (
                  <li key={p.label} className="flex items-center gap-3.5">
                    <div className="h-10 w-10 rounded-full bg-[#fff0e9] flex items-center justify-center text-[#1c2e3a]"><Icon size={18} /></div>
                    <span className="text-[15px] font-medium text-slate-700">{p.label}</span>
                  </li>
                );
              })}
            </ul>
            <Button onClick={() => onEnquire()} className="mt-7 w-full bg-[#1c2e3a] hover:bg-[#29424f] text-white rounded-md h-12 font-semibold">
              Enquire for Bulk Supply
            </Button>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#f7f9fc] py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-extrabold text-[#1c2e3a]">{s.value}</p>
              <p className="mt-2 text-slate-500 text-[14px] font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
